class CartGifts extends HTMLElement {
  constructor() {
    super();
    this.giftCards = [];
    this.currentCart = null;
    this.isProcessingRemoval = false;
    this.updateTimeout = null;
    this.pendingUpdate = false;
  }

  connectedCallback() {
    // Add a small delay to ensure element is fully connected before initializing
    setTimeout(() => {
      this.init();
    }, 50);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:updated', this.handleCartUpdate);
    document.removeEventListener('click', this.handleClick);
    this.removeEventListener('force-update', this.handleForceUpdate);
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }
  }

  init() {
    this.bindEvents();
    this.cacheElements();
    this.updateGiftCardsVisibility();
  }

  cacheElements() {
    this.giftCards = this.querySelectorAll('.cart-gift-cards');
  }

  bindEvents() {
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleForceUpdate = this.handleForceUpdate.bind(this);

    document.addEventListener('cart:updated', this.handleCartUpdate);
    document.addEventListener('click', this.handleClick);
    this.addEventListener('force-update', this.handleForceUpdate);
  }

  handleForceUpdate(event) {
    // Force re-cache elements and update with new cart data
    this.cacheElements();
    this.updateGiftCardsVisibility(event.detail?.cart);
  }

  handleCartUpdate(event) {
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }
    
    if (this.isProcessingRemoval) {
      this.pendingUpdate = true;
      return;
    }

    if (event?.detail?.source === 'gift-removal') {
      return;
    }

    const cartData = event?.detail?.cart || null;
    
    this.updateTimeout = setTimeout(() => {
      this.updateGiftCardsVisibility(cartData);
    }, 50); 
  }

  handleClick(event) {
    if (event.target.matches('.cart-gift-card__select-btn')) {
      this.handleGiftSelect(event);
    }
    
    if (event.target.matches('.cart-item__swap-gift')) {
      this.handleSwapGift(event);
    }
    
    if (event.target.matches('.cart-item__remove-gift')) {
      this.handleRemoveGift(event);
    }
  }

  handleGiftSelect(event) {
    const button = event.target;
    const giftCard = button.closest('.cart-gift-card');
    const giftCardsContainer = button.closest('.cart-gift-cards');
    
    if (!giftCard || !giftCardsContainer) {
      console.error('Could not find gift card or container');
      return;
    }
    
    const tier = giftCardsContainer.dataset.tier || 'tier_1';
    const productsData = giftCardsContainer.dataset.products;
    const threshold = giftCard.dataset.thresholdRaw || '75';
    const thresholdFormatted = giftCard.dataset.threshold || '£75.00';
    const giftNumber = giftCard.dataset.giftNumber || '1';
    const actualTier = giftCard.dataset.actualTier || tier;
    
    let products = [];
    try {
      products = JSON.parse(productsData || '[]');
    } catch (e) {
      console.error('Error parsing products data:', e);
      return;
    }
    
    const giftDrawer = document.querySelector('gift-drawer');
    if (giftDrawer && products.length > 0) {
      giftDrawer.open(button, {
        products: products,
        tier: actualTier,
        threshold: threshold,
        thresholdFormatted: thresholdFormatted,
        giftNumber: giftNumber
      });
    }
  }

  async getCurrentCart(providedCart = null) {
    if (providedCart) {
      this.currentCart = providedCart;
      return providedCart;
    }
    
    try {
      const response = await fetch('/cart.js');
      this.currentCart = await response.json();
      return this.currentCart;
    } catch (error) {
      console.error('Error fetching cart:', error);
      return null;
    }
  }

  getGiftItemsInCart(cart) {
    if (!cart || !cart.items) return [];
    
    return cart.items.filter(item => 
      item.properties && 
      item.properties._gift === 'true' &&
      item.properties._gift_tier // Ensure it has a tier (more specific check)
    );
  }

  getGiftItemByTier(cart, tier) {
    const giftItems = this.getGiftItemsInCart(cart);
    return giftItems.find(item => 
      item.properties && item.properties._gift_tier === tier
    );
  }

  getGiftThresholds() {
    const thresholds = {};
    
    const tier1Threshold = parseFloat(
      this.dataset.tier1Threshold || 
      this.dataset['tier-1-threshold'] ||
      this.getAttribute('data-tier-1-threshold')
    );
    
    const tier2Threshold = parseFloat(
      this.dataset.tier2Threshold || 
      this.dataset['tier-2-threshold'] ||
      this.getAttribute('data-tier-2-threshold')
    );
    
    const tier3Threshold = parseFloat(
      this.dataset.tier3Threshold || 
      this.dataset['tier-3-threshold'] ||
      this.getAttribute('data-tier-3-threshold')
    );

    const tier4Threshold = parseFloat(
      this.dataset.tier4Threshold ||
      this.dataset['tier-4-threshold'] ||
      this.getAttribute('data-tier-4-threshold')
    );
    
    if (!isNaN(tier1Threshold) && tier1Threshold > 0) {
      thresholds['tier_1'] = tier1Threshold * 100;
    }
    
    if (!isNaN(tier2Threshold) && tier2Threshold > 0) {
      thresholds['tier_2'] = tier2Threshold * 100;
    }
    
    if (!isNaN(tier3Threshold) && tier3Threshold > 0) {
      thresholds['tier_3'] = tier3Threshold * 100;
    }

    if (!isNaN(tier4Threshold) && tier4Threshold > 0) {
      thresholds['tier_4'] = tier4Threshold * 100;
    }
    
    return thresholds;
  }

  async removeInvalidGifts(cart) {
    if (this.isProcessingRemoval) {
      return false;
    }

    this.isProcessingRemoval = true;
    
    try {
      const thresholds = this.getGiftThresholds();
      
      if (Object.keys(thresholds).length === 0) {
        return false;
      }
      
      const giftItems = this.getGiftItemsInCart(cart);
      
      if (giftItems.length === 0) {
        return false;
      }
      
      // Calculate cart total excluding gift items
      const nonGiftItems = cart.items.filter(item => 
        !item.properties || item.properties._gift !== 'true'
      );
      
      const cartTotalCents = nonGiftItems.reduce((total, item) => {
        return total + (item.line_price || (item.price * item.quantity));
      }, 0);
      
      // Find gifts that should be removed
      const giftsToRemove = giftItems.filter(giftItem => {
        const tier = giftItem.properties._gift_tier;
        const requiredThreshold = thresholds[tier];
        
        if (requiredThreshold === undefined) {
          return false;
        }
        
        const belowThreshold = cartTotalCents < requiredThreshold;
        
        return belowThreshold;
      });
      
      if (giftsToRemove.length === 0) {
        return false;
      }
      
      // Remove gifts with the working method
      return await this.performGiftRemoval(giftsToRemove);
      
    } finally {
      this.isProcessingRemoval = false;
      
      if (this.pendingUpdate) {
        this.pendingUpdate = false;
        setTimeout(() => {
          this.updateGiftCardsVisibility();
        }, 50);
      }
    }
  }

  async performGiftRemoval(giftsToRemove) {
    let removedAny = false;
    
    for (const giftItem of giftsToRemove) {
      // Get fresh cart data right before removal
      const freshCart = await this.getCurrentCart();
      if (!freshCart) {
        console.error('❌ Could not get fresh cart data');
        continue;
      }

      // Find the current item in the fresh cart
      const currentGiftItem = freshCart.items.find(item => 
        item.variant_id === giftItem.variant_id && 
        item.properties && 
        item.properties._gift === 'true' &&
        item.properties._gift_tier === giftItem.properties._gift_tier
      );

      if (!currentGiftItem) {
        continue;
      }

      // Use the working removal method (item key)
      const success = await this.removeGiftByKey(currentGiftItem);
      
      if (success) {
        removedAny = true;
        // CRITICAL: Wait between removals to avoid conflicts - this is essential!
        await new Promise(resolve => setTimeout(resolve, 50));
      } else {
        console.error(`❌ Failed to remove ${giftItem.product_title}`);
      }
    }

    if (removedAny) {
      // Trigger comprehensive cart refresh for immediate UI update
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { source: 'gift-removal', force: true }
      }));
      
      // Also trigger cart drawer refresh if available
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        setTimeout(async () => {
          const updatedCart = await fetch('/cart.js').then(r => r.json());
          const drawerHtml = await fetch('/cart?section_id=cart-drawer').then(r => r.text());
          const html = new DOMParser().parseFromString(drawerHtml, 'text/html');
          const buttonToRemove = html.querySelector('.btn-continue-shipping');
          buttonToRemove?.remove();

          const cleanedHtml = html.documentElement.outerHTML;

          cartDrawer.renderContents({ sections: { 'cart-drawer': cleanedHtml } });
        }, 50);
      }
    }
    
    return removedAny;
  }

  async removeGiftByKey(giftItem) {
    if (!giftItem.key) {
      console.error(`❌ No key found for ${giftItem.product_title}, cannot remove`);
      return false;
    }

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: giftItem.key,
          quantity: 0
        })
      });

      if (response.ok) {
        const result = await response.json();
        return true;
      } else {
        const errorText = await response.text();
        console.error(`❌ Removal failed (${response.status}):`, errorText);
        return false;
      }

    } catch (error) {
      console.error(`❌ Removal error:`, error);
      return false;
    }
  }

  async updateGiftCardsVisibility(providedCart = null) {
    
    if (this.isProcessingRemoval) {
      this.pendingUpdate = true;
      return;
    }
    this.dispatchCheckoutBlock(true);
    const cart = await this.getCurrentCart(providedCart);
    if (!cart) {
      return;
    }

    // Remove invalid gifts first
    const removedGifts = await this.removeInvalidGifts(cart);
    
    // If we removed gifts, get fresh cart data - CRITICAL for no-refresh updates
    let currentCart = cart;
    if (removedGifts) {
      await new Promise(resolve => setTimeout(resolve, 50));
      currentCart = await this.getCurrentCart();
      if (!currentCart) {
        return;
      }
    }

    let availableGiftTiers = 0;

    this.giftCards.forEach(giftCardsContainer => {
      const tier = giftCardsContainer.dataset.tier;
      const giftInCart = this.getGiftItemByTier(currentCart, tier);
      
      if (giftInCart) {
        giftCardsContainer.style.display = 'none';
      } else {
        giftCardsContainer.style.display = 'flex';
        availableGiftTiers++;
      }
    });

    const pickerTitle = this.querySelector('.cart-gifts__picker--title');
    if (pickerTitle) {
      pickerTitle.style.display = availableGiftTiers > 0 ? '' : 'none';
    }

    this.updateGiftLineItems(currentCart);
    this.cacheElements();
    this.dispatchCheckoutBlock(false);
  }
  dispatchCheckoutBlock(isBlocked) {
    document.dispatchEvent(new CustomEvent('cart:block-checkout', {
      detail: { isBlocked }
    }));
  }

  updateGiftLineItems(cart) {
    const giftItems = this.getGiftItemsInCart(cart);
    
    giftItems.forEach(giftItem => {
      // Be more specific - look for the exact line item by index or key
      const lineItem = document.querySelector(`cart-item[data-index="${giftItem.index}"]`) ||
                      document.querySelector(`[data-index="${giftItem.index}"]`) ||
                      document.querySelector(`cart-item[data-key="${giftItem.key}"]`) ||
                      document.querySelector(`[data-key="${giftItem.key}"]`);
      
      // Only convert if we found the specific line item and it's not already converted
      if (lineItem && !lineItem.classList.contains('cart-item--gift')) {
        // Double-check this is actually a gift by verifying the properties
        if (this.isActualGiftItem(giftItem)) {
          this.convertToGiftLineItem(lineItem, giftItem);
        }
      }
    });
  }

  isActualGiftItem(item) {
    return item.properties && 
          item.properties._gift === 'true' && 
          item.properties._gift_tier;
  }

  convertToGiftLineItem(lineItem, giftItem) {
    lineItem.classList.add('cart-item--gift');
    
    // Only modify price elements within THIS specific line item
    const priceElements = lineItem.querySelectorAll('.cart-item__totals, .cart-item__price, .price');
    priceElements.forEach(priceElement => {
      // Add a more specific check to ensure we're only modifying the right elements
      if (priceElement && !priceElement.classList.contains('price-item--regular')) {
        priceElement.innerHTML = '<span class="cart-item__price--free">Free</span>';
      }
    });
    
    const removeButton = lineItem.querySelector('.cart-item__remove, .remove-button, [data-cart-remove]');
    const controlsArea = lineItem.querySelector('.cart-item__controls, .cart-item__actions');
    
    if (removeButton || controlsArea) {
      const giftControls = this.createGiftControls(giftItem);
      
      if (removeButton) {
        removeButton.parentNode.replaceChild(giftControls, removeButton);
      } else if (controlsArea) {
        controlsArea.appendChild(giftControls);
      }
    }
  }

  createGiftControls(giftItem) {
    const controls = document.createElement('div');
    controls.className = 'cart-item__gift-controls';
    
    const tier = giftItem.properties._gift_tier;
    
    controls.innerHTML = `
      <button type="button" 
              class="cart-item__swap-gift btn btn--secondary btn--small" 
              data-tier="${tier}"
              data-variant-id="${giftItem.variant_id}"
              data-line-index="${giftItem.index}">
        Swap Gift
      </button>
      <button type="button" 
              class="cart-item__remove-gift btn btn--tertiary btn--small" 
              data-tier="${tier}"
              data-variant-id="${giftItem.variant_id}"
              data-line-index="${giftItem.index}">
        Remove
      </button>
    `;
    
    return controls;
  }

  async handleSwapGift(event) {
    const button = event.target;
    const tier = button.dataset.tier;
    const lineIndex = parseInt(button.dataset.lineIndex);
    
    const giftCardsContainer = this.querySelector(`[data-tier="${tier}"]`);
    if (!giftCardsContainer) {
      console.error(`No gift card container found for tier: ${tier}`);
      return;
    }
    
    const productsData = giftCardsContainer.dataset.products;
    const giftCard = giftCardsContainer.querySelector('.cart-gift-card');
    
    let products = [];
    try {
      products = JSON.parse(productsData || '[]');
    } catch (e) {
      console.error('Error parsing products data:', e);
      return;
    }
    
    const threshold = giftCard?.dataset.thresholdRaw || '75';
    
    const giftDrawer = document.querySelector('gift-drawer');
    if (giftDrawer && products.length > 0) {
      giftDrawer.open(button, {
        products: products,
        tier: tier,
        threshold: threshold,
        isSwapping: true,
        swapLineIndex: lineIndex
      });
    }
  }

  async handleRemoveGift(event) {
    const button = event.target;
    const lineIndex = parseInt(button.dataset.lineIndex);
    
    const originalText = button.textContent;
    button.textContent = 'Removing...';
    button.disabled = true;
    
    try {
      const cartItems = document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
      
      if (cartItems && typeof cartItems.updateQuantity === 'function') {
        cartItems.updateQuantity(lineIndex, 0);
      } else {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            line: lineIndex,
            quantity: 0
          })
        });
        
        if (!response.ok) {
          throw new Error('Failed to remove gift from cart');
        }
        
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
      
    } catch (error) {
      console.error('Error removing gift:', error);
      button.textContent = originalText;
      button.disabled = false;
    }
  }

  refreshCartDrawer() {
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }

  refresh() {
    this.updateGiftCardsVisibility();
  }

  async forceCheckThresholds() {
    this.isProcessingRemoval = false;
    await this.updateGiftCardsVisibility();
  }

  async manuallyTriggerRemoval() {
    this.isProcessingRemoval = false;
    await this.updateGiftCardsVisibility();
  }
}

customElements.define('cart-gifts', CartGifts);
